'use client'

import { PopoverAnchor } from '@radix-ui/react-popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  Input,
  Popover,
  PopoverContent,
  Skeleton,
} from '@repo/ui/components'
import { cn } from '@repo/ui/lib'
import { Command as CommandPrimitive } from 'cmdk'
import { Check } from 'lucide-react'
import { ReactNode, useEffect, useState } from 'react'

import { useAutoComplete } from './use-auto-complete.ts' // Adjust the path accordingly

type Props<T> = {
  className?: string
  data: T[] // The data to display in the autocomplete
  emptyMessage?: ReactNode | string // Custom message when no items are found
  getDisplayValue: (item: T) => string // Function to derive the display value after selection
  getSearchValue: (item: T) => string // Function to derive the search string from an item
  isLoading?: boolean // Indicates if the data is loading
  onSearchValueChange: (value: string) => void // Callback when the search input changes
  onSelectedValueChange: (value?: T) => void // Callback when an item is selected
  placeholder?: string // Placeholder text for the input
  renderOption: (item: T) => ReactNode // Function to render each option
  searchStartLength?: number
  searchValue: string // Current search input value
  selectedValue: null | T // Currently selected value
}

export function AutoComplete<T>({
  className,
  data,
  emptyMessage = 'No items.',
  getDisplayValue,
  getSearchValue,
  isLoading,
  onSearchValueChange,
  onSelectedValueChange,
  placeholder = 'Search...',
  renderOption,
  searchStartLength = 0,
  searchValue,
  selectedValue,
}: Props<T>) {
  const { displayValue, onInputBlur, onSelectItem, open, setOpen } = useAutoComplete<T>({
    data,
    getDisplayValue,
    getSearchValue,
    onSearchValueChange,
    onSelectedValueChange,
    searchValue,
    selectedValue,
  })
  const [searchTerm, setSearchTerm] = useState(displayValue)

  useEffect(() => {
    if (selectedValue) {
      setSearchTerm(displayValue)
    }
  }, [selectedValue])

  useEffect(() => {
    const timeout = setTimeout(() => {
      onSearchValueChange(searchTerm)
    }, 500)

    return () => clearTimeout(timeout)
  }, [searchTerm])

  return (
    <div className={cn('flex items-center', className)}>
      <Popover onOpenChange={setOpen} open={open}>
        <Command shouldFilter={false}>
          <PopoverAnchor asChild>
            <CommandPrimitive.Input
              asChild
              onBlur={onInputBlur}
              onMouseDown={() => setOpen(() => !!searchValue)}
              onValueChange={search => {
                if (searchValue.length >= searchStartLength - 1) setOpen(true)
                else setOpen(false)

                setSearchTerm(search)
              }}
              value={searchTerm}
            >
              <Input placeholder={placeholder} />
            </CommandPrimitive.Input>
          </PopoverAnchor>
          <PopoverContent
            asChild
            className="p-0"
            onInteractOutside={e => {
              if (e.target instanceof Element && e.target.hasAttribute('cmdk-input')) {
                e.preventDefault()
              }
            }}
            onOpenAutoFocus={e => e.preventDefault()}
            style={{
              width: 'var(--radix-popover-trigger-width)',
            }}
          >
            <CommandList>
              {isLoading && (
                <CommandPrimitive.Loading>
                  <div className="p-1">
                    <Skeleton className="h-12 w-full" />
                  </div>
                </CommandPrimitive.Loading>
              )}
              {!isLoading && data.length > 0 && (
                <CommandGroup>
                  {data.map(item => {
                    const key = getSearchValue(item)
                    const isSelected = selectedValue && getSearchValue(selectedValue) === key
                    return (
                      <CommandItem
                        key={key}
                        onMouseDown={e => e.preventDefault()}
                        onSelect={() => onSelectItem(key)}
                        value={key}
                      >
                        <Check
                          className={cn('mr-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')}
                        />
                        {renderOption(item)}
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              )}
              {!isLoading && data.length === 0 && <CommandEmpty>{emptyMessage}</CommandEmpty>}
            </CommandList>
          </PopoverContent>
        </Command>
      </Popover>
    </div>
  )
}
